import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const themes = {
  default: {
    name: 'Default',
    colors: {
      background: '#171717',
      cardBackground: '#1e1e1e',
      primary: '#42f56f',
      secondary: '#31db8a',
      text: '#f5f5f5',
      textSecondary: '#888',
      border: '#333',
      inputBackground: '#1e1e1e',
      inputBorder: '#555',
      dropdownBackground: '#1e1e1e',
      dropdownHover: '#2a2a2a',
      tooltipBackground: '#333',
      success: '#42f56f',
      error: '#ff4444',
      warning: '#ffb700',
      info: '#42a5f5',
      previewButtonBackground: '#fcb023',
      previewButtonHover: '#FF8C00',
      previewButtonDisabled: '#cc7a00',
      previewButtonDisabledText: 'rgba(0, 0, 0, 0.7)'
    },
    backgroundType: 'color'
  },
  neonSynth: {
    name: 'Neon Synth',
    colors: {
      background: '#0a0a14',
      cardBackground: '#12121f',
      primary: '#00ffd5',
      secondary: '#00c4a7',
      text: '#ffffff',
      textSecondary: '#a0a0c0',
      border: '#1f1f3d',
      inputBackground: '#16162b',
      inputBorder: '#2a2a4d',
      dropdownBackground: '#16162b',
      dropdownHover: '#1f1f3d',
      tooltipBackground: '#16162b',
      success: '#00ffd5',
      error: '#ff2d6c',
      warning: '#ffb300',
      info: '#00a2ff',
      previewButtonBackground: '#ff2d6c',
      previewButtonHover: '#ff0055',
      previewButtonDisabled: '#7a1533',
      previewButtonDisabledText: 'rgba(255, 255, 255, 0.7)'
    },
    backgroundType: 'color'
  }
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('selectedTheme');
    return savedTheme && themes[savedTheme] ? savedTheme : 'default';
  });

  const [customBackground, setCustomBackground] = useState(() => {
    return localStorage.getItem('customBackgroundImage') || null;
  });

  useEffect(() => {
    localStorage.setItem('selectedTheme', currentTheme);
    if (currentTheme === 'customBackground' && customBackground) {
      localStorage.setItem('customBackgroundImage', customBackground);
    }
  }, [currentTheme, customBackground]);

  const applyTheme = (themeId) => {
    if (!themes[themeId]) return;
    
    const theme = themes[themeId];
    const root = document.documentElement;
    
    // Apply colors
    Object.entries(theme.colors).forEach(([property, value]) => {
      root.style.setProperty(`--${property}`, value);
    });

    // Handle background
    if (theme.backgroundType === 'color') {
      root.style.setProperty('--background', theme.colors.background);
      root.style.removeProperty('--background-image');
      document.body.style.backgroundImage = 'none';
    } else if (theme.backgroundType === 'image' && customBackground) {
      document.body.style.cssText = `
        background-image: url(${customBackground});
        background-size: cover;
        background-position: center;
        background-attachment: fixed;
        transition: all 0.3s ease;
      `;
    }

    setCurrentTheme(themeId);
  };

  const handleCustomBackground = (imageUrl) => {
    setCustomBackground(imageUrl);
    themes.customBackground.backgroundImage = imageUrl;
    applyTheme('customBackground');
  };

  return (
    <ThemeContext.Provider value={{
      currentTheme,
      setTheme: applyTheme,
      setCustomBackground: handleCustomBackground,
      themes
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
